import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Input, PageHeader, Accordion } from '@mts/ui';
import * as Avataaars from 'avataaars';
import { Heart, Shuffle } from 'lucide-react';
import { authClient } from '../lib/authClient';
import { resetPostHogIdentity } from '../lib/posthog';
import { getApiBaseUrl } from '../services/api';
import { fetchOwnProfile, updateOwnProfileDisplayName } from '../services/userProfiles';
import { fetchFavorites, removeFavorite } from '../services/favorites';
import { getGeneratedDisplayName } from '../services/identityDisplayName';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import ProfileMobileStickyNav from '../components/profile/ProfileMobileStickyNav';
import { PROFILE_SECTIONS, normalizeProfileSection } from '../components/profile/profileSections';
import { buildListingPath } from '../utils/listingUrls';
import { redirectToPortalWithHandoff } from '../utils/portalUrls';
import { usePublicTenant } from '../hooks/usePublicTenant';

const Avatar = [
  Avataaars.default?.default,
  Avataaars['module.exports']?.default,
  Avataaars.default,
  Avataaars.Avatar,
].find((candidate) => typeof candidate === 'function');

function formatDate(value) {
  if (!value) return 'Unknown';
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) return 'Unknown';
  return asDate.toLocaleString();
}

function buildSessionSummary(session = null) {
  if (!session) {
    return [];
  }

  return [
    { label: 'Session Expires', value: formatDate(session.expiresAt) },
    { label: 'IP Address', value: session.ipAddress || 'Unavailable' },
    { label: 'User Agent', value: session.userAgent || 'Unavailable' },
  ];
}

function formatMemberSince(value) {
  if (!value) return 'Member since unavailable';
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) return 'Member since unavailable';

  return `Member since ${asDate.toLocaleString(undefined, { month: 'long', year: 'numeric' })}`;
}

function getRoleKey(value) {
  return String(value?.role?.key || value?.role || '').trim().toLowerCase();
}

const ROLE_PRIORITY = {
  system_admin: 100,
  system_staff: 90,
  tenant_owner: 80,
  tenant_admin: 70,
  tenant_staff: 60,
  tenant_viewer: 50,
  business_owner: 40,
  business_staff: 35,
  business_user: 30,
  subscriber: 10,
};

function formatRoleLabel(roleKey) {
  return String(roleKey || '')
    .split('_')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ') || 'No role assigned';
}

function summarizeRoles(memberships) {
  const roleKeys = [...new Set((Array.isArray(memberships) ? memberships : []).map(getRoleKey).filter(Boolean))]
    .sort((a, b) => (ROLE_PRIORITY[b] || 0) - (ROLE_PRIORITY[a] || 0));
  return {
    label: formatRoleLabel(roleKeys[0]),
    additionalCount: Math.max(0, roleKeys.length - 1),
  };
}

function getTenantManageOrigin(listing) {
  const tenant = listing?.tenant || {};
  const customDomain = (Array.isArray(tenant.customDomains) ? tenant.customDomains : [])
    .find((domain) => domain?.isActive === true || String(domain?.status || '').toUpperCase() === 'ACTIVE');
  const customHost = String(customDomain?.domain || customDomain?.wwwDomain || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  if (customHost) return `https://${customHost}`;

  const settings = Array.isArray(tenant.tenantSettings) ? tenant.tenantSettings : [];
  const configuredUrl = ['tenant.website_url', 'tenant.custom_domain', 'tenant.primary_domain']
    .map((key) => settings.find((setting) => setting?.settingKey === key)?.settingValue)
    .map((value) => typeof value === 'string' ? value.trim() : '')
    .find(Boolean);
  if (configuredUrl) {
    try {
      return new URL(/^https?:\/\//i.test(configuredUrl) ? configuredUrl : `https://${configuredUrl}`).origin;
    } catch {
      // Fall through to the current tenant-facing origin.
    }
  }

  return typeof window !== 'undefined' ? window.location.origin : '';
}

function parseAvatarValue(rawValue) {
  if (!rawValue) {
    return { config: null, imageUrl: null };
  }

  if (typeof rawValue === 'object') {
    return { config: rawValue, imageUrl: null };
  }

  if (typeof rawValue !== 'string') {
    return { config: null, imageUrl: null };
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (parsed && typeof parsed === 'object') {
      return { config: parsed, imageUrl: null };
    }
  } catch {
    // Non-JSON avatar strings are treated as image URLs.
  }

  return { config: null, imageUrl: rawValue };
}

const AVATAR_OPTION_SETS = {
  avatarStyle: ['Circle'],
  topType: [
    'NoHair',
    'Hat',
    'Hijab',
    'Turban',
    'LongHairBigHair',
    'LongHairBun',
    'LongHairCurly',
    'LongHairStraight',
    'LongHairMiaWallace',
    'ShortHairDreads01',
    'ShortHairFrizzle',
    'ShortHairShortCurly',
    'ShortHairShortFlat',
    'ShortHairTheCaesar',
    'ShortHairTheCaesarSidePart',
  ],
  accessoriesType: ['Blank', 'Kurt', 'Prescription01', 'Round', 'Sunglasses', 'Wayfarers'],
  hairColor: ['Auburn', 'Black', 'Blonde', 'Brown', 'BrownDark', 'Red', 'SilverGray'],
  facialHairType: ['Blank', 'BeardMedium', 'BeardLight', 'MoustacheFancy'],
  facialHairColor: ['Auburn', 'Black', 'Blonde', 'Brown', 'BrownDark', 'Red'],
  clotheType: ['BlazerShirt', 'GraphicShirt', 'Hoodie', 'Overall', 'ShirtCrewNeck', 'ShirtVNeck'],
  clotheColor: ['Black', 'Blue01', 'Gray01', 'Heather', 'PastelBlue', 'PastelGreen', 'White'],
  eyeType: ['Default', 'Happy', 'Wink', 'Squint', 'Surprised'],
  eyebrowType: ['Default', 'DefaultNatural', 'RaisedExcited', 'UpDown', 'AngryNatural'],
  mouthType: ['Default', 'Smile', 'Serious', 'Twinkle'],
  skinColor: ['Light', 'Pale', 'Tanned', 'Brown', 'DarkBrown', 'Black'],
};

function generateRandomAvatarConfig() {
  return Object.fromEntries(
    Object.entries(AVATAR_OPTION_SETS).map(([key, values]) => {
      const selected = values[Math.floor(Math.random() * values.length)];
      return [key, selected];
    })
  );
}

function ProfilePage() {
  const toast = useFeedback();
  const { section: rawSection } = useParams();
  const navigate = useNavigate();
  const activeSection = normalizeProfileSection(rawSection);
  const activeSectionMeta = PROFILE_SECTIONS.find((section) => section.id === activeSection);
  const { spotlightName } = usePublicTenant();
  const { data: session, isPending } = authClient.useSession();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingError, setLoadingError] = useState('');
  const [profilePayload, setProfilePayload] = useState(null);
  const [legalNameInput, setLegalNameInput] = useState('');
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');

  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [pendingAvatarConfig, setPendingAvatarConfig] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState('');
  const [portalOpeningListingId, setPortalOpeningListingId] = useState('');

  useEffect(() => {
    let ignore = false;

    async function loadProfile() {
      if (isPending) {
        return;
      }

      try {
        setLoadingProfile(true);
        setLoadingError('');
        const payload = await fetchOwnProfile();
        if (ignore) return;
        setProfilePayload(payload);
        setLegalNameInput(payload?.profile?.name || session?.user?.name || '');
        setDisplayNameInput(payload?.profile?.displayName || '');
      } catch (error) {
        if (ignore) return;
        setProfilePayload(null);
        setLoadingError(error?.message || 'Unable to load your profile.');
      } finally {
        if (!ignore) {
          setLoadingProfile(false);
        }
      }
    }

    loadProfile();

    return () => {
      ignore = true;
    };
  }, [isPending, session?.user?.id, session?.user?.name]);

  const profile = profilePayload?.profile || null;
  const sessionInfo = profilePayload?.session || null;
  const { config: avatarConfig, imageUrl: avatarImageUrl } = parseAvatarValue(profile?.avatar);
  const previewAvatarConfig = pendingAvatarConfig || avatarConfig;
  const authenticatedUserId = profile?.id || session?.user?.id || null;
  const authenticatedUserEmail = profile?.email || session?.user?.email || null;
  const identityName = profile?.name || session?.user?.name || 'Add your legal name';
  const legalNameValue = profile?.name || session?.user?.name || '';
  const isLegalNameMissing = !legalNameValue;
  const identityHandle = profile?.displayName ? `@${profile.displayName}` : 'No display name set';
  const identitySpotlight = spotlightName || 'Unknown Spotlight';
  const memberSince = formatMemberSince(profile?.createdAt || sessionInfo?.createdAt || session?.session?.createdAt);
  const fullBreadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Profile', href: '/profile' },
    { label: activeSectionMeta?.label || 'Profile Information' },
  ];
  const collapsedBreadcrumbItems = [
    { label: 'Profile', href: '/profile' },
    { label: activeSectionMeta?.label || 'Profile Information' },
  ];

  const memberships = Array.isArray(profile?.memberships) ? profile.memberships : [];
  const managedListings = Array.isArray(profile?.managedListings) ? profile.managedListings : [];
  const roleSummary = useMemo(() => summarizeRoles(memberships), [memberships]);

  const roleGroups = useMemo(() => {
    const subscriberMemberships = memberships.filter((membership) => membership?.role?.key === 'subscriber');
    const administrationMemberships = memberships.filter((membership) => {
      const key = String(membership?.role?.key || '').toLowerCase();
      return key.includes('admin') || key.includes('manager') || key === 'tenant_owner';
    });

    return {
      subscriberMemberships,
      administrationMemberships,
      managedListings,
    };
  }, [memberships, managedListings]);

  const handleOpenBusinessOwnerDashboard = async (listing) => {
    const listingId = listing?.id;
    const normalizedListingId = String(listingId || '').trim();
    if (!normalizedListingId) return;

    const listingSlug = String(listing?.slug || normalizedListingId).trim();

    try {
      setPortalOpeningListingId(normalizedListingId);
      await redirectToPortalWithHandoff(
        navigate,
        `/manage/listings/${encodeURIComponent(listingSlug)}`,
        { targetOrigin: getTenantManageOrigin(listing) },
      );
    } finally {
      setPortalOpeningListingId('');
    }
  };

  const handleProfileSave = async (event) => {
    event.preventDefault();
    setDisplayNameError('');


    const trimmed = String(displayNameInput || '').trim().toLowerCase();
    if (!/^[a-z0-9-]{3,32}$/.test(trimmed)) {
      setDisplayNameError('Display name must be 3-32 chars, lowercase letters, numbers, or hyphen.');
      return;
    }

    const normalizedLegalName = String(legalNameInput || '').trim().replace(/\s+/g, ' ');
    if (normalizedLegalName.length > 120) {
      setDisplayNameError('Legal name must be 120 characters or fewer.');
      return;
    }

    try {
      setSavingDisplayName(true);
      const payload = await updateOwnProfileDisplayName({
        displayName: trimmed,
        name: normalizedLegalName || null,
        avatar: pendingAvatarConfig ? JSON.stringify(pendingAvatarConfig) : undefined,
      });
      const updatedUser = payload?.user || {};

      setProfilePayload((prev) => ({
        ...(prev || {}),
        profile: {
          ...(prev?.profile || {}),
          displayName: updatedUser.displayName,
          name: updatedUser.name,
          email: updatedUser.email,
          avatar: updatedUser.avatar,
          status: updatedUser.status,
        },
      }));
      setLegalNameInput(updatedUser.name || '');
      setDisplayNameInput(updatedUser.displayName || trimmed);
      setPendingAvatarConfig(null);
      toast.success('Profile updated.', { id: 'action:ProfilePage:handleProfileSave' });
      setLastSavedAt(new Date().toISOString());
    } catch (error) {
      toast.error(error?.message || 'Unable to update display name.', { id: 'action:ProfilePage:handleProfileSave' });
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleGenerateDisplayName = () => {
    const seedBase = authenticatedUserId || authenticatedUserEmail || 'profile';
    const generated = getGeneratedDisplayName(`${seedBase}-${Date.now()}`);
    setDisplayNameInput(generated);
    setDisplayNameError('');
    toast.success('Generated a new display name.', { id: 'action:ProfilePage:handleGenerateDisplayName' });
  };

  const handleGenerateAvatarPreview = () => {
    setPendingAvatarConfig(generateRandomAvatarConfig());
    setDisplayNameError('');
    toast.success('Generated a random avatar. Save Profile to apply it.', { id: 'action:ProfilePage:handleGenerateAvatarPreview' });
  };

  useEffect(() => {
    if (activeSection !== 'favorites' || !authenticatedUserId) return;

    let cancelled = false;
    setFavoritesLoading(true);
    setFavoritesError('');

    fetchFavorites()
      .then((data) => {
        if (!cancelled) setFavorites(Array.isArray(data?.favorites) ? data.favorites : []);
      })
      .catch((err) => {
        if (!cancelled) setFavoritesError(err?.message || 'Unable to load favorites.');
      })
      .finally(() => {
        if (!cancelled) setFavoritesLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeSection, authenticatedUserId]);

  async function handleRemoveFavorite(listingId) {
    try {
      await removeFavorite(listingId);
      setFavorites((prev) => prev.filter((f) => f.listing?.id !== listingId));
    } catch {
      // Ignore — the heart state stays as-is.
    }
  }

  const handleLogout = async () => {
    try {
      await authClient.signOut();
    } catch (_error) {
      // Best effort sign out.
    }
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/portal-token/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_error) {
      // Best effort sign out.
    }
    resetPostHogIdentity();
    setProfilePayload(null);
    navigate('/login', { replace: true });
  };

  if (isPending || loadingProfile) {
    return (
      <div className="min-h-screen bg-background px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <Card padding="lg" className="border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p style={{ color: 'var(--page-body-color)' }}>Loading your profile...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!authenticatedUserId) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-5">
        <div
          className="flex flex-col gap-4 rounded-xl border px-4 py-4 md:flex-row md:items-start md:justify-between"
          style={{
            backgroundColor: 'var(--surface)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border"
              style={{
                borderColor: 'color-mix(in srgb, var(--primary) 28%, var(--border))',
                backgroundColor: 'color-mix(in srgb, var(--primary) 12%, var(--surface))',
                color: 'var(--primary)',
              }}
              aria-label="Avatar"
            >
              {Avatar && avatarConfig ? (
                <Avatar
                  style={{ width: '100%', height: '100%' }}
                  {...avatarConfig}
                />
              ) : avatarImageUrl ? (
                <img
                  src={avatarImageUrl}
                  alt="User avatar"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-xs font-semibold">NA</span>
              )}
            </div>

            <div className="space-y-1">
              <p
                className="font-ui text-xl font-semibold"
                style={{ color: isLegalNameMissing ? 'var(--page-body-color)' : 'var(--page-heading-color)' }}
              >
                {identityName}
              </p>
              <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                {identityHandle}
              </p>
              <div className="flex flex-wrap items-center gap-2 text-sm" style={{ color: 'var(--page-body-color)' }}>
                <Badge variant="default">{roleSummary.label}</Badge>
                {roleSummary.additionalCount > 0 ? <Badge variant="default">+{roleSummary.additionalCount} more</Badge> : null}
                <span>• {identitySpotlight}</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                {memberSince}
              </p>
            </div>
          </div>

          <div className="md:pt-1">
            <div className="hidden md:block">
              <PageHeader.Breadcrumbs
                items={fullBreadcrumbItems}
                className="mb-0"
                aria-label="Profile breadcrumb"
              />
            </div>
            <div className="md:hidden">
              <PageHeader.Breadcrumbs
                items={collapsedBreadcrumbItems}
                className="mb-0"
                aria-label="Profile breadcrumb"
              />
            </div>
          </div>
        </div>

        <ProfileMobileStickyNav sections={PROFILE_SECTIONS} activeSection={activeSection} />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px,1fr]">
          <ProfileSidebar sections={PROFILE_SECTIONS} activeSection={activeSection} />

          <div className="space-y-4">
            {loadingError ? (
              <Card
                padding="md"
                className="border"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'color-mix(in srgb, var(--danger) 30%, var(--border))',
                  color: 'var(--danger)',
                }}
              >
                {loadingError}
              </Card>
            ) : null}

            {activeSection === 'settings' ? (
              <Card padding="lg" shadow="sm" className="border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h2 className="font-ui text-xl font-semibold" style={{ color: 'var(--page-heading-color)' }}>Profile Information</h2>
                <p className="mt-1 text-sm" style={{ color: 'var(--page-body-color)' }}>
                  Your email and legal name come from authentication identity. Display name is your editable public handle.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--page-body-color)' }}>Email</p>
                    <p className="mt-1 text-sm" style={{ color: 'var(--page-heading-color)' }}>{profile?.email || session?.user?.email || 'Unknown'}</p>
                  </div>
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--page-body-color)' }}>Name</p>
                    {legalNameValue ? (
                      <p className="mt-1 text-sm" style={{ color: 'var(--page-heading-color)' }}>{legalNameValue}</p>
                    ) : (
                      <p className="mt-1 text-sm italic" style={{ color: 'var(--page-body-color)' }}>
                        Add your legal name to complete your profile.
                      </p>
                    )}
                  </div>
                </div>

                <form className="mt-4 space-y-3" onSubmit={handleProfileSave}>
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--page-body-color)' }}>
                      Avatar
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border"
                        style={{
                          borderColor: 'color-mix(in srgb, var(--primary) 28%, var(--border))',
                          backgroundColor: 'color-mix(in srgb, var(--primary) 12%, var(--surface))',
                          color: 'var(--primary)',
                        }}
                        aria-label="Avatar preview"
                      >
                        {Avatar && previewAvatarConfig ? (
                          <Avatar style={{ width: '100%', height: '100%' }} {...previewAvatarConfig} />
                        ) : avatarImageUrl ? (
                          <img src={avatarImageUrl} alt="User avatar" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs font-semibold">NA</span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Button type="button" variant="secondary" onClick={handleGenerateAvatarPreview} disabled={savingDisplayName}>
                          Generate Random Avatar
                        </Button>
                        <p className="text-xs" style={{ color: 'var(--page-body-color)' }}>
                          Preview updates immediately. Save Profile to keep this avatar.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Input
                    label="Legal Name"
                    type="text"
                    value={legalNameInput}
                    onChange={(event) => setLegalNameInput(event.target.value)}
                    placeholder="e.g. Akash Desai"
                    disabled={savingDisplayName}
                    className="border-[var(--ui-input-border)] text-[var(--ui-input-text)] focus:border-[var(--primary)] focus:ring-[var(--primary)]"
                    helperText="Used for account identity and member records."
                  />

                  <div className="relative">
                    <Input
                      label="Display Name"
                      type="text"
                      value={displayNameInput}
                      onChange={(event) => setDisplayNameInput(event.target.value)}
                      placeholder="e.g. roswell-local"
                      disabled={savingDisplayName}
                      className="border-[var(--ui-input-border)] pr-11 text-[var(--ui-input-text)] focus:border-[var(--primary)] focus:ring-[var(--primary)]"
                      helperText="Visible publicly in reviews, comments, and community activity."
                      error={Boolean(displayNameError)}
                      errorMessage={displayNameError || undefined}
                    />
                    <button
                      type="button"
                      onClick={handleGenerateDisplayName}
                      disabled={savingDisplayName}
                      className="absolute right-3 top-[35px] inline-flex h-7 w-7 items-center justify-center rounded-md border"
                      style={{
                        borderColor: 'var(--border)',
                        color: 'var(--page-body-color)',
                        backgroundColor: 'var(--surface)',
                      }}
                      aria-label="Generate display name"
                      title="Generate display name"
                    >
                      <Shuffle size={14} />
                    </button>
                  </div>



                  <Button type="submit" loading={savingDisplayName} disabled={savingDisplayName}>
                    {savingDisplayName ? 'Saving...' : 'Save Profile'}
                  </Button>
                  <p className="text-xs" style={{ color: 'var(--page-body-color)' }}>
                    Last Saved Date: {lastSavedAt ? formatDate(lastSavedAt) : 'Not yet saved'}
                  </p>
                </form>
              </Card>
            ) : null}

            {activeSection === 'security' ? (
              <Card padding="lg" shadow="sm" className="border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h2 className="font-ui text-xl font-semibold" style={{ color: 'var(--page-heading-color)' }}>Session & Security</h2>
                <p className="mt-1 text-sm" style={{ color: 'var(--page-body-color)' }}>
                  Active session details and sign-out action for this browser.
                </p>

                <div className="mt-4 space-y-2">
                  {buildSessionSummary(sessionInfo).map((row) => (
                    <div key={row.label} className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--page-body-color)' }}>{row.label}</p>
                      <p className="mt-1 text-sm break-all" style={{ color: 'var(--page-heading-color)' }}>{row.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <Button variant="secondary" onClick={handleLogout}>Logout</Button>
                  <Link to="/login" className="inline-flex items-center text-sm font-medium underline" style={{ color: 'var(--primary)' }}>
                    Sign in with another account
                  </Link>
                </div>
              </Card>
            ) : null}

            {activeSection === 'memberships' ? (
              <Card padding="lg" shadow="sm" className="border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h2 className="font-ui text-xl font-semibold" style={{ color: 'var(--page-heading-color)' }}>Memberships & Roles</h2>
                <p className="mt-1 text-sm" style={{ color: 'var(--page-body-color)' }}>
                  Breakdown of your role access by function.
                </p>

                <div className="mt-4">
                  <Accordion type="multiple" variant="card" defaultValue={['subscriber']}>
                    <Accordion.Item
                      value="subscriber"
                      title="Subscriber"
                      subtitle="Businesses and communities followed by this account"
                      badge={<Badge variant="default">{roleGroups.subscriberMemberships.length || 1}</Badge>}
                    >
                      {roleGroups.subscriberMemberships.length === 0 ? (
                        <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                          No subscriber communities found.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {roleGroups.subscriberMemberships.map((membership) => (
                            <div key={membership.id} className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                                {membership?.tenant?.name || 'Platform'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </Accordion.Item>

                    {roleGroups.managedListings.length > 0 ? (
                      <Accordion.Item
                        value="business-owner"
                        title="Business Owner"
                        subtitle="Businesses you manage or have a pending claim for"
                        badge={<Badge variant="default">{roleGroups.managedListings.length}</Badge>}
                      >
                        <div className="space-y-2">
                          {roleGroups.managedListings.map((item) => {
                            const listing = item?.listing || null;
                            const listingId = String(listing?.id || '').trim();
                            const isOpening = portalOpeningListingId === listingId;

                            return (
                              <div key={item.id || listingId} className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium" style={{ color: 'var(--page-heading-color)' }}>
                                      {listing?.name || 'Unnamed business'}
                                    </p>
                                    <p className="text-xs" style={{ color: 'var(--page-body-color)' }}>
                                      {listing?.tenant?.name || 'Tenant unavailable'} · {String(item?.status || '').toUpperCase() === 'PENDING' ? 'Claim pending' : 'Approved'}
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    disabled={!listingId || isOpening}
                                    onClick={() => handleOpenBusinessOwnerDashboard(listing)}
                                  >
                                    {isOpening ? 'Opening...' : 'Open in Manage'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Accordion.Item>
                    ) : null}

                    <Accordion.Item
                      value="moderator"
                      title="Moderator"
                      subtitle="Coming soon"
                      badge={<Badge variant="default">Coming soon</Badge>}
                      disabled
                    >
                      {null}
                    </Accordion.Item>

                    {roleGroups.administrationMemberships.length > 0 ? (
                      <Accordion.Item
                        value="administration"
                        title="Administration"
                        subtitle="Tenant and platform administration memberships"
                        badge={<Badge variant="default">{roleGroups.administrationMemberships.length}</Badge>}
                      >
                        <div className="space-y-2">
                          {roleGroups.administrationMemberships.map((membership) => (
                            <div key={membership.id} className="rounded-lg border p-3" style={{ borderColor: 'var(--border)' }}>
                              <p className="text-sm font-medium" style={{ color: 'var(--page-heading-color)' }}>
                                {membership?.role?.name || membership?.role?.key || 'Unknown role'}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--page-body-color)' }}>
                                {membership?.tenant?.name || 'Platform'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </Accordion.Item>
                    ) : null}
                  </Accordion>
                </div>
              </Card>
            ) : null}

            {activeSection === 'favorites' ? (
              <Card padding="lg" shadow="sm" className="border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h2 className="font-ui text-xl font-semibold" style={{ color: 'var(--page-heading-color)' }}>My Favorites</h2>
                <p className="mt-1 text-sm" style={{ color: 'var(--page-body-color)' }}>
                  Businesses you have saved across this Spotlight.
                </p>

                {favoritesLoading ? (
                  <p className="mt-4 text-sm" style={{ color: 'var(--page-body-color)' }}>Loading favorites...</p>
                ) : favoritesError ? (
                  <InlineFeedback variant="error" className="mt-4 text-sm" style={{ color: 'var(--danger)' }}>{favoritesError}</InlineFeedback>
                ) : favorites.length === 0 ? (
                  <div className="mt-6 flex flex-col items-center gap-3 py-8 text-center">
                    <Heart size={32} style={{ color: 'var(--page-body-color)', opacity: 0.35 }} />
                    <p className="text-sm" style={{ color: 'var(--page-body-color)' }}>
                      You haven&apos;t saved any businesses yet.
                    </p>
                    <Link
                      to="/listings"
                      className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium"
                      style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                    >
                      Browse the Directory
                    </Link>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {favorites.map((fav) => {
                      const listing = fav.listing;
                      if (!listing) return null;
                      const category = listing.listingCategories?.[0]?.tenantCategory?.name;
                      const location = [listing.city, listing.state].filter(Boolean).join(', ');
                      const initials = String(listing.name || '')
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join('') || '?';

                      return (
                        <div
                          key={fav.id || listing.id}
                          className="flex items-start gap-3 rounded-lg border p-3"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          <div
                            className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border text-xs font-semibold"
                            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--page-body-color)' }}
                          >
                            {listing.logoUrl ? (
                              <img
                                src={listing.logoUrl}
                                alt={`${listing.name} logo`}
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : initials}
                          </div>

                          <div className="min-w-0 flex-1">
                            <Link
                              to={buildListingPath(listing) || '/listings'}
                              className="block truncate text-sm font-semibold hover:underline"
                              style={{ color: 'var(--page-heading-color)' }}
                            >
                              {listing.name}
                            </Link>
                            {category ? (
                              <p className="truncate text-xs" style={{ color: 'var(--page-body-color)' }}>{category}</p>
                            ) : null}
                            {location ? (
                              <p className="truncate text-xs" style={{ color: 'var(--page-body-color)' }}>{location}</p>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            aria-label="Remove from favorites"
                            onClick={() => handleRemoveFavorite(listing.id)}
                            className="shrink-0 rounded-md p-1.5"
                            style={{ color: 'var(--primary)' }}
                          >
                            <Heart size={15} fill="currentColor" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
